import React, { useEffect, useState, useCallback } from 'react';
import { useToast } from '../hooks/use-toast';
import { Loader2, LogOut, RefreshCw, ShieldCheck } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = 'rb_admin_token';

const STATUS_OPTIONS = [
  { value: 'pending_payment', label: 'Pending Payment' },
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'assigned', label: 'Driver Assigned' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
];

const statusColor = (s) => {
  switch (s) {
    case 'confirmed': return 'text-green-400';
    case 'completed': return 'text-blue-400';
    case 'assigned': return 'text-orange-400';
    case 'cancelled': return 'text-red-400';
    default: return 'text-gray-400';
  }
};

const Admin = () => {
  const { toast } = useToast();
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || '');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loggingIn, setLoggingIn] = useState(false);
  const [rides, setRides] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState(null);

  const loadRides = useCallback(async (tk) => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/rides`, {
        headers: { Authorization: `Bearer ${tk}` },
      });
      if (res.status === 401 || res.status === 403) {
        localStorage.removeItem(TOKEN_KEY);
        setToken('');
        toast({ title: 'Session expired', description: 'Please sign in again.', variant: 'destructive' });
        return;
      }
      const data = await res.json();
      setRides(Array.isArray(data) ? data : []);
    } catch (e) {
      toast({ title: 'Failed to load bookings', description: 'Please try again.', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    if (token) loadRides(token);
  }, [token, loadRides]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoggingIn(true);
    try {
      const res = await fetch(`${API}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Login failed');
      if (data.user?.role !== 'admin') {
        throw new Error('This account is not an administrator.');
      }
      localStorage.setItem(TOKEN_KEY, data.access_token);
      setToken(data.access_token);
    } catch (err) {
      toast({ title: 'Sign in failed', description: err.message, variant: 'destructive' });
    } finally {
      setLoggingIn(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken('');
    setRides([]);
  };

  const updateRide = async (id, patch) => {
    setSavingId(id);
    try {
      const res = await fetch(`${API}/api/admin/rides/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(patch),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Update failed');
      setRides((prev) => prev.map((r) => (r.id === id ? data : r)));
      toast({ title: 'Saved', description: 'Booking updated.' });
    } catch (err) {
      toast({ title: 'Update failed', description: err.message, variant: 'destructive' });
    } finally {
      setSavingId(null);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center px-6">
        <form onSubmit={handleLogin} className="w-full max-w-sm" data-testid="admin-login-form">
          <div className="flex items-center gap-2 mb-8">
            <ShieldCheck className="h-6 w-6 text-orange-500" />
            <span className="text-sm tracking-[0.3em] uppercase text-gray-400">RideBuraq Admin</span>
          </div>
          <h1 className="text-3xl font-semibold mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>Sign in</h1>
          <input
            data-testid="admin-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Admin email"
            className="w-full mb-3 rounded-xl bg-white/[0.04] border border-white/10 px-4 h-12 text-white placeholder-gray-500 focus:border-orange-500 focus:outline-none"
            required
          />
          <input
            data-testid="admin-password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password"
            className="w-full mb-6 rounded-xl bg-white/[0.04] border border-white/10 px-4 h-12 text-white placeholder-gray-500 focus:border-orange-500 focus:outline-none"
            required
          />
          <button
            type="submit"
            data-testid="admin-login-btn"
            disabled={loggingIn}
            className="w-full rounded-full py-3.5 font-semibold text-white bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 disabled:opacity-50 flex items-center justify-center"
          >
            {loggingIn ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Enter Dashboard'}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      <header className="flex items-center justify-between px-5 md:px-8 py-5 border-b border-white/10 sticky top-0 bg-black/90 backdrop-blur z-10">
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-5 w-5 text-orange-500" />
          <span className="text-sm tracking-[0.25em] uppercase text-gray-300">Admin · Bookings</span>
          <span className="ml-3 text-xs text-gray-500">{rides.length} total</span>
        </div>
        <div className="flex items-center gap-3">
          <button data-testid="admin-refresh" onClick={() => loadRides(token)} className="flex items-center gap-1.5 text-sm text-gray-300 hover:text-white">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
          <button data-testid="admin-logout" onClick={handleLogout} className="flex items-center gap-1.5 text-sm text-gray-300 hover:text-white">
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>
      </header>

      <div className="p-4 md:p-6">
        {loading && rides.length === 0 ? (
          <div className="flex items-center justify-center py-24 text-gray-400"><Loader2 className="h-6 w-6 animate-spin mr-2" /> Loading bookings…</div>
        ) : rides.length === 0 ? (
          <div className="text-center py-24 text-gray-500" data-testid="admin-empty">No bookings yet.</div>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-white/10">
            <table className="w-full text-sm min-w-[1100px]">
              <thead>
                <tr className="text-left text-gray-400 border-b border-white/10 bg-white/[0.02]">
                  <th className="px-4 py-3 font-medium">Booked</th>
                  <th className="px-4 py-3 font-medium">Customer</th>
                  <th className="px-4 py-3 font-medium">Route</th>
                  <th className="px-4 py-3 font-medium">Vehicle</th>
                  <th className="px-4 py-3 font-medium">Pickup Date/Time</th>
                  <th className="px-4 py-3 font-medium">Fare</th>
                  <th className="px-4 py-3 font-medium">Payment</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium">Driver</th>
                </tr>
              </thead>
              <tbody>
                {rides.map((r) => (
                  <tr key={r.id} data-testid={`admin-ride-${r.id}`} className="border-b border-white/5 hover:bg-white/[0.02] align-top">
                    <td className="px-4 py-3 text-gray-400 whitespace-nowrap">{r.createdAt ? new Date(r.createdAt).toLocaleString() : '—'}</td>
                    <td className="px-4 py-3">
                      <div className="text-white">{r.customerName || '—'}</div>
                      <div className="text-gray-500 text-xs">{r.customerEmail || '—'}</div>
                      <div className="text-gray-500 text-xs">{r.customerPhone || 'No phone'}</div>
                    </td>
                    <td className="px-4 py-3 max-w-[260px]">
                      <div className="text-gray-200"><span className="text-green-500">●</span> {r.pickupAddress || '—'}</div>
                      <div className="text-gray-200"><span className="text-red-500">●</span> {r.dropoffAddress || '—'}</div>
                      {r.notes ? <div className="text-gray-500 text-xs mt-1 italic">“{r.notes}”</div> : null}
                      {r.preferences && Object.keys(r.preferences).length > 0 ? (
                        <div className="text-orange-400/70 text-xs mt-1">{Object.values(r.preferences).join(' · ')}</div>
                      ) : null}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div>{r.vehicleClass || '—'}</div>
                      {r.tripType ? <div className="text-gray-500 text-xs capitalize">{r.tripType.replace(/_/g, ' ')}</div> : null}
                      {r.bookingMode === 'hourly' ? <div className="text-orange-400/70 text-xs">Hourly · {r.hours || '—'}h</div> : null}
                      {(r.flightNumber || r.airline) ? <div className="text-blue-400/70 text-xs">✈ {[r.airline, r.flightNumber].filter(Boolean).join(' ')}</div> : null}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div>{r.scheduledDate || '—'}</div>
                      <div className="text-gray-500 text-xs">{r.scheduledTime || ''}</div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">${Number(r.estimatedFare || 0).toFixed(2)}</td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={r.paymentStatus === 'paid' ? 'text-green-400' : 'text-gray-400'}>{r.paymentStatus || 'pending'}</span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <select
                        data-testid={`status-select-${r.id}`}
                        value={r.status}
                        onChange={(e) => updateRide(r.id, { status: e.target.value })}
                        disabled={savingId === r.id}
                        className={`bg-white/[0.04] border border-white/10 rounded-lg px-2 py-1.5 focus:border-orange-500 focus:outline-none ${statusColor(r.status)}`}
                      >
                        {STATUS_OPTIONS.map((o) => (
                          <option key={o.value} value={o.value} className="bg-black text-white">{o.label}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <input
                        data-testid={`driver-input-${r.id}`}
                        defaultValue={r.driver || ''}
                        placeholder="Assign driver"
                        onBlur={(e) => {
                          const v = e.target.value.trim();
                          if (v !== (r.driver || '')) updateRide(r.id, { driver: v });
                        }}
                        className="bg-white/[0.04] border border-white/10 rounded-lg px-2 py-1.5 w-36 text-white placeholder-gray-500 focus:border-orange-500 focus:outline-none"
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default Admin;
